import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonGroupVariants = cva(
  "flex w-fit items-stretch *:focus-visible:relative *:focus-visible:z-10 has-[>[data-slot=button-group]]:gap-2 [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  {
    variants: {
      orientation: {
        horizontal:
          '[&>[data-slot]:not(:first-child)]:rounded-l-none [&>[data-slot]:not(:first-child)]:before:rounded-l-none [&>[data-slot]:not(:first-child)]:after:rounded-l-none [&>[data-slot]:not(:last-child)]:rounded-r-none [&>[data-slot]:not(:last-child)]:before:rounded-r-none [&>[data-slot]:not(:last-child)]:after:rounded-r-none [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:-ml-px',
        vertical:
          'flex-col [&>[data-slot]:not(:first-child)]:rounded-t-none [&>[data-slot]:not(:first-child)]:before:rounded-t-none [&>[data-slot]:not(:first-child)]:after:rounded-t-none [&>[data-slot]:not(:last-child)]:rounded-b-none [&>[data-slot]:not(:last-child)]:before:rounded-b-none [&>[data-slot]:not(:last-child)]:after:rounded-b-none [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:-mt-px',
      },
      shape: {
        square: '',
        round: '',
      },
      size: {
        small: '',
        medium: '',
        large: '',
      },
    },
    compoundVariants: [
      {
        orientation: 'horizontal',
        shape: 'square',
        size: 'small',
        class:
          '[&>[data-slot]:first-child]:rounded-l-sm [&>[data-slot]:first-child]:before:rounded-l-sm! [&>[data-slot]:first-child]:after:rounded-l-sm! [&>[data-slot]:last-child]:rounded-r-sm [&>[data-slot]:last-child]:before:rounded-r-sm! [&>[data-slot]:last-child]:after:rounded-r-sm!',
      },
      {
        orientation: 'horizontal',
        shape: 'square',
        size: ['medium', 'large'],
        class:
          '[&>[data-slot]:first-child]:rounded-l-md [&>[data-slot]:first-child]:before:rounded-l-md! [&>[data-slot]:first-child]:after:rounded-l-md! [&>[data-slot]:last-child]:rounded-r-md [&>[data-slot]:last-child]:before:rounded-r-md! [&>[data-slot]:last-child]:after:rounded-r-md!',
      },
      {
        orientation: 'horizontal',
        shape: 'round',
        class:
          '[&>[data-slot]:first-child]:rounded-l-full [&>[data-slot]:first-child]:before:rounded-l-full! [&>[data-slot]:first-child]:after:rounded-l-full! [&>[data-slot]:last-child]:rounded-r-full [&>[data-slot]:last-child]:before:rounded-r-full! [&>[data-slot]:last-child]:after:rounded-r-full!',
      },
      {
        orientation: 'vertical',
        shape: 'square',
        size: 'small',
        class:
          '[&>[data-slot]:first-child]:rounded-t-sm [&>[data-slot]:first-child]:before:rounded-t-sm! [&>[data-slot]:first-child]:after:rounded-t-sm! [&>[data-slot]:last-child]:rounded-b-sm [&>[data-slot]:last-child]:before:rounded-b-sm! [&>[data-slot]:last-child]:after:rounded-b-sm!',
      },
      {
        orientation: 'vertical',
        shape: 'square',
        size: ['medium', 'large'],
        class:
          '[&>[data-slot]:first-child]:rounded-t-md [&>[data-slot]:first-child]:before:rounded-t-md! [&>[data-slot]:first-child]:after:rounded-t-md! [&>[data-slot]:last-child]:rounded-b-md [&>[data-slot]:last-child]:before:rounded-b-md! [&>[data-slot]:last-child]:after:rounded-b-md!',
      },
      {
        orientation: 'vertical',
        shape: 'round',
        class:
          '[&>[data-slot]:first-child]:rounded-t-full [&>[data-slot]:first-child]:before:rounded-t-full! [&>[data-slot]:first-child]:after:rounded-t-full! [&>[data-slot]:last-child]:rounded-b-full [&>[data-slot]:last-child]:before:rounded-b-full! [&>[data-slot]:last-child]:after:rounded-b-full!',
      },
    ],
    defaultVariants: {
      orientation: 'horizontal',
      shape: 'square',
      size: 'medium',
    },
  },
);

export type ButtonGroupProps = React.ComponentProps<'div'> &
  VariantProps<typeof buttonGroupVariants>;

const ButtonGroup = ({
  className,
  orientation,
  shape = 'square',
  size = 'medium',
  ...props
}: ButtonGroupProps) => {
  return (
    <div
      role="group"
      data-slot="button-group"
      data-orientation={orientation}
      data-shape={shape}
      data-size={size}
      className={cn(buttonGroupVariants({ orientation, shape, size }), className)}
      {...props}
    />
  );
};

export default ButtonGroup;
